import Minze, { MinzeElement, Attrs, Reactive, EventListeners } from 'minze'
import {
  reauthenticateWithCredential,
  updatePassword,
  GoogleAuthProvider,
  FacebookAuthProvider,
  EmailAuthProvider,
  unlink,
  linkWithRedirect,
} from 'firebase/auth'
import { EMAIL_REGEX, PASSWORD_REGEX, unescapeHtml, getVisibleWidth } from '../../modules/utils'
import { Routing } from '../../modules/routing'
import { Locale } from '../../modules/locale'
import MainModule from '../../modules/main'
import { Callback } from '../../modules/callback'
import { Font, SCREEN_WIDTHS } from '../../constants'

Locale.getInstance().registerKeySet('fr', 'lvcAccountSecurity', {
  title: 'Connexion et sécurité',
  errors: {
    oldPassword: 'Veuillez entrer votre mot de passe actuel',
    incorrectOldPassword: 'Le mot de passe actuel est incorrect',
    newPassword: 'Veuillez entrer un mot de passe valide',
    confirmPassword: 'Les mots de passe ne correspondent pas',
    passwordNotRespected:
      'Votre mot de passe doit contenir au moins 8 caractères, une majuscule, une minuscule, un chiffre et un caractère spécial',
    passwordLength: 'Le mot de passe doit contenir au moins 8 caractères',
    misc: 'Une erreur est survenue',
    changeEmail: {
      password: 'Veuillez entrer votre mot de passe',
      newEmail: 'Veuillez entrer un courriel valide',
      confirmNewEmail: 'Les deux courriels ne correspondent pas',
    },
  },
  providers: {
    google: 'Google',
    facebook: 'Facebook',
    email: 'Email',
    notLinked: 'Non lié',
  },
  providerConnection: 'Connexion lié avec mon profil',
  account: 'Compte',
  legalInformationTitle: 'Informations légales',
  termsAndConditions: 'Termes et conditions',
  privacyPolicy: 'Politique de confidentialité',
  save: 'Sauvegarder',
  cancel: 'Annuler',
  modify: 'Modifier',
  createPassword: 'Créer un mot de passe',
  deleteAccount: 'Supprimer mon compte',
  deleteAccountConfirmation:
    'Êtes-vous sûr de vouloir supprimer votre compte? Cette action est irréversible.',
  deleteAccountConfirmationModalTitle: 'Supprimer mon compte',
  deleteAccountConfirmationModalText:
    'Êtes-vous certains de vouloir supprimer votre compte? Cette action est irréversible.',
  deleteAccountConfirmationModalInputLabel: 'Confirmer mon mot de passe',
  deleteAccountConfirmationModalInputPlaceholder: 'Entrez votre mot de passe',
  deleteAccountConfirmationModalPasswordError: 'Le mot de passe saisi est incorrect',
  deleteAccountConfirmationModalCancelButton: 'Annuler',
  deleteAccountConfirmationModalConfirmButton: 'Supprimer mon compte',
  delete: 'Supprimer',
  deleteAccountTermsAndConditionsMode: {
    title: "RETIRER MON CONSENTEMENT AUX CONDITIONS D'UTILISATION",
    text: "Pour préserver votre compte à Lavitrine.com, vous devez consentir aux Conditions d'utilisation. Si vous retirez votre consentement aux Conditions d'utilisation de Lavitrine.com, vous devrez supprimer votre compte.",
    linkLabel: 'En savoir plus…',
    linkUrl: `${MainModule.getInstance().getRouting().getRoute('termsOfUse')}`,
    tooltipTitle: "Conditions d'utilisation",
    tooltipText:
      "Vous avez consenti aux conditions d'utilisation en créant un compte sur Lavitrine.com. Si vous désirez retirer votre consentement, vous devez supprimer votre compte. Pour procéder, veuillez décocher la case des conditions d'utilisation.",
    tooltipLink: 'En savoir plus',
    tooltipUrl: `${MainModule.getInstance().getRouting().getRoute('termsOfUse')}`,
  },
  deleteAccountPrivacyPolicyMode: {
    title: 'RETIRER MON CONSENTEMENT À LA POLITIQUE DE CONFIDENTIALITÉ',
    text: 'Pour préserver votre compte à Lavitrine.com, vous devez consentir à la Politique de confidentialité. Si vous retirez votre consentement à la Politique de confidentialité de Lavitrine.com, vous devrez supprimer votre compte. Si vous cherchez plutôt à modifier vos préférences de suivi des cookies, cliquez sur le lien “Changer mes préférences” ci-dessous.',
    link1Label: 'En savoir plus…',
    link1Url: `${MainModule.getInstance().getRouting().getRoute('privacyPolicy')}`,
    link2Label: 'Changer mes préférences',
    link2Url: 'Axept.io',
    tooltipTitle: 'Politique de confidentialité',
    tooltipText:
      'Vous avez consenti à la politique de confidentialité en créant un compte sur Lavitrine.com. Si vous désirez retirer votre consentement, vous devez supprimer votre compte. Pour procéder, veuillez décocher la case à droite de la politique de confidentialité.',
    tooltipLink: 'En savoir plus',
    tooltipUrl: `${MainModule.getInstance().getRouting().getRoute('privacyPolicy')}`,
  },
  unlinkProviderErrorModal: {
    title: {
      'google.com': 'Délier son compte Google',
      'facebook.com': 'Délier son compte Facebook',
    },
    text: {
      'google.com':
        "Votre compte a été créé à l'aide de l'authentification unique (SSO), vous devez vous créer un mot de passe avant de pouvoir délier votre compte Google. Voulez-vous créer votre mot de passe maintenant?",
      'facebook.com':
        "Votre compte a été créé à l'aide de l'authentification unique (SSO), vous devez vous créer un mot de passe avant de pouvoir délier votre compte Facebook. Voulez-vous créer votre mot de passe maintenant?",
    },
    buttons: {
      cancel: 'Annuler',
      submit: 'Créer un mot de passe',
    },
  },
  link: 'Lier',
  unlink: 'Délier',
  notLinked: 'Non lié',
  email: 'Courriel',
  password: 'Mot de passe',
  oldPassword: 'Mot de passe actuel',
  newPassword: 'Nouveau mot de passe',
  confirmPassword: 'Confirmer le nouveau mot de passe',
  passwordChanged: 'Votre mot de passe a été changé',
  clickOnModify: 'Cliquez sur le bouton modifier pour changer votre mot de passe',
  changeEmail: {
    password: 'Entrer votre mot de passe',
    newEmail: 'Nouveau courriel',
    confirmNewEmail: 'Confirmer le nouveau courriel',
    emailSentNotification: 'Courriel de confirmation envoyé au nouveau courriel',
  },
  createPasswordNotification: 'Lien de création de mot de passe envoyé à votre courriel',
})

Locale.getInstance().registerKeySet('en', 'lvcAccountSecurity', {
  title: 'Connection and security',
  errors: {
    oldPassword: 'Please enter your current password',
    incorrectOldPassword: 'The current password is incorrect',
    newPassword: 'Please enter a valid password',
    confirmPassword: 'Passwords do not match',
    passwordNotRespected:
      'Your password must have at least 8 characters, one uppercase letter, one lowercase letter, one number and one special character',
    passwordLength: 'The password must contain at least 8 characters',
    misc: 'An error occurred',
    changeEmail: {
      password: 'Please enter your password',
      newEmail: 'Please enter a valid email',
      confirmNewEmail: 'The emails do not match',
    },
  },
  providers: {
    google: 'Google',
    facebook: 'Facebook',
    email: 'Email',
    notLinked: 'Not linked',
  },
  providerConnection: 'Connection linked with my profile',
  account: 'Account',
  legalInformationTitle: 'Legal information',
  termsAndConditions: 'Terms and conditions',
  privacyPolicy: 'Privacy Policy',
  save: 'Save',
  cancel: 'Cancel',
  modify: 'Change',
  createPassword: 'Create a password',
  deleteAccount: 'Delete my account',
  deleteAccountConfirmation:
    'Are you sure you want to delete your account? This action is irreversible.',
  deleteAccountConfirmationModalTitle: 'Delete my account',
  deleteAccountConfirmationModalText:
    'Are you sure you want to delete your account? This is an  irreversible action.',
  deleteAccountConfirmationModalInputLabel: 'Confirm your password',
  deleteAccountConfirmationModalInputPlaceholder: 'Enter your password',
  deleteAccountConfirmationModalPasswordError: 'The password you entered is incorrect',
  deleteAccountConfirmationModalCancelButton: 'Cancel',
  deleteAccountConfirmationModalConfirmButton: 'Delete my account',
  delete: 'Delete',
  deleteAccountTermsAndConditionsMode: {
    title: 'WITHDRAW MY CONSENT TO THE TERMS AND CONDITIONS',
    text: 'To preserve your account at Lavitrine.com, you must agree to the Terms and conditions. If you withdraw your consent to the Lavitrine.com Terms and conditions, you will need to delete your account.',
    linkLabel: 'I want to know more…',
    linkUrl: `${MainModule.getInstance().getRouting().getRoute('termsOfUse')}`,
    tooltipTitle: 'Terms and conditions',
    tooltipText:
      'You have given your consent to the terms and conditions when you created your account on Lavitrine.com. If you desire to withdraw your consent, you will need to delete your account. To do so, uncheck the toggle to the right of the terms and conditions.',
    tooltipLink: 'Learn more',
    tooltipUrl: `${MainModule.getInstance().getRouting().getRoute('termsOfUse')}`,
  },
  deleteAccountPrivacyPolicyMode: {
    title: 'WITHDRAW MY CONSENT TO THE PRIVACY POLICY',
    text: 'To preserve your account at Lavitrine.com, you must agree to the Privacy policy. If you withdraw your consent to the Lavitrine.com Privacy policy, you will need to delete your account. If you are looking to change your cookies consent settings, click on the “Change my preferences” link below instead.',
    link1Label: 'I want to know more…',
    link1Url: `${MainModule.getInstance().getRouting().getRoute('privacyPolicy')}`,
    link2Label: 'Change my preferences',
    link2Url: 'https://www.axept.io/',
    tooltipTitle: 'Privacy policy',
    tooltipText:
      'You have given your consent to the privacy policy when you created your account on Lavitrine.com. If you desire to withdraw your consent, you will need to delete your account. To do so, disable the toggle to the right of the privacy policy.',
    tooltipLink: 'Learn more',
    tooltipUrl: `${MainModule.getInstance().getRouting().getRoute('privacyPolicy')}`,
  },
  unlinkProviderErrorModal: {
    title: {
      'google.com': 'Unlink your Google account',
      'facebook.com': 'Unlink your Facebook account',
    },
    text: {
      'google.com':
        'Your account was created through SSO, you will need to create login credentials before you can unlink your Google account. Do you want to create your password now?',
      'facebook.com':
        'Your account was created through SSO, you will need to create login credentials before you can unlink your Facebook account. Do you want to create your password now?',
    },
    buttons: {
      cancel: 'Cancel',
      submit: 'Create a password',
    },
  },
  link: 'Link',
  unlink: 'Unlink',
  notLinked: 'Not linked',
  email: 'Email',
  password: 'Password',
  oldPassword: 'Current password',
  newPassword: 'New password',
  confirmPassword: 'Confirm new password',
  passwordChanged: 'Password changed',
  clickOnModify: 'Click on the modify button to change your password',
  changeEmail: {
    password: 'Enter your password',
    newEmail: 'New email',
    confirmNewEmail: 'Confirm new email',
    emailSentNotification: 'Confirmation e-mail sent to new e-mail address',
  },
  createPasswordNotification: 'Password creation link has been sent to your email',
})

const initialErrorsState = {
  oldPassword: false,
  newPassword: false,
  confirmPassword: false,
  changeEmail: {
    password: false,
    newEmail: false,
    confirmNewEmail: false,
  },
}

export interface LvcAccountSecurity {
  titleSeparator: boolean
}

export class LvcAccountSecurity extends MinzeElement {
  attrs: Attrs = [['title-separator', false]]
  static observedAttributes = ['titleSeparator']
  private toggleCb: string = Callback.attach(this.onProviderToggle.bind(this))
  private onChangeCb: string = ''
  private onCreatePasswordBusy: boolean = false
  reactive: Reactive = [
    ['isDesktop', true],
    ['email', ''],
    ['changePasswordDrawer', true],
    ['changeEmailDrawer', true],
    ['providers', []],
    ['unlinkProviderErrorModalOpened', false],
    ['unlinkingProvider', ''],
    ['deleteConfirmationPasswordError', false],
    ['privacyPolicyTooltipOpened', false],
    ['termsAndConditionsTooltipOpened', false],
  ]
  errors: any = initialErrorsState

  updateInfo() {
    const mainModule = MainModule.getInstance()
    const userAuth = mainModule.getAuth().getUserAuth()

    if (userAuth && userAuth.email) {
      this.email = userAuth.email
      let providers: any[] = []
      if (userAuth.providerData) providers = [...userAuth.providerData]
      this.providers = providers
    } else {
      this.email = ''
    }
    this.rerender()
  }

  onChange() {
    // Get values of all the inputs
    const inputIds = [
      'oldPassword',
      'newPassword',
      'confirmPassword',
      'newEmailPassword',
      'newEmail',
      'confirmNewEmail',
    ]
    const values = inputIds.reduce(
      (acc, id) => {
        const element = this.select(`[name=\"${id}\"]`) as HTMLInputElement
        acc[id] = element?.value || ''
        return acc
      },
      {} as { [key: string]: string }
    )

    // Check if there are any non-empty values
    const hasValues = Object.values(values).some(value => value.trim() !== '')

    // Set the page to dirty or clearDirty accordingly
    const mainModule = MainModule.getInstance()
    const routing = mainModule.getRouting()
    if (hasValues) {
      routing.setDirty()
    } else {
      routing.clearDirty()
    }
  }

  onReady(): void | Promise<void> {
    this.isDesktop = getVisibleWidth() > SCREEN_WIDTHS.DESTKOP_LARGE
    this.updateInfo()
    this.onChangeCb = Callback.attach(this.onChange.bind(this))
  }

  handleAuthChanged = (event: Event) => {
    this.updateInfo()
  }

  onPasswordDrawerToggle = (event: Event) => {
    this.changePasswordDrawer = !this.changePasswordDrawer
    this.rerender()
    const oldPasswordElement = this.select('[name="oldPassword"]') as HTMLInputElement
    const newPasswordElement = this.select('[name="newPassword"]') as HTMLInputElement
    const confirmPasswordElement = this.select('[name="confirmPassword"]') as HTMLInputElement
    oldPasswordElement.value = ''
    newPasswordElement.value = ''
    confirmPasswordElement.value = ''
    this.errors = {
      ...this.errors,
      oldPassword: false,
      newPassword: false,
      confirmPassword: false,
    }
  }

  onEmailDrawerToggle = (event: Event) => {
    this.changeEmailDrawer = !this.changeEmailDrawer
    this.rerender()
    const passwordElement = this.select('[name="newEmailPassword"]') as HTMLInputElement
    const newPasswordElement = this.select('[name="newPassword"]') as HTMLInputElement
    const confirmPasswordElement = this.select('[name="confirmPassword"]') as HTMLInputElement
    passwordElement.value = ''
    newPasswordElement.value = ''
    confirmPasswordElement.value = ''
    this.errors.changeEmail = initialErrorsState.changeEmail
  }

  onInputChange = (event: Event) => {
    for (const key in this.errors) {
      this.errors[key] = false
    }
    this.rerender()
  }

  changeEmail = async () => {
    const mainModule = MainModule.getInstance()
    const auth = mainModule.getAuth()
    const userAuth = mainModule.getAuth().getUserAuth()
    const email = userAuth.email
    const passwordElement = this.select('[name="newEmailPassword"]') as HTMLInputElement
    const newEmailElement = this.select('[name="newEmail"]') as HTMLInputElement
    const confirmNewEmailElement = this.select('[name="confirmNewEmail"]') as HTMLInputElement
    const password = unescapeHtml(passwordElement.value)
    const newEmail = unescapeHtml(newEmailElement.value)
    const confirmNewEmail = unescapeHtml(confirmNewEmailElement.value)
    for (const key in this.errors.changeEmail) {
      this.errors.changeEmail[key] = false
    }

    if (newEmail !== confirmNewEmail) {
      this.errors.changeEmail.confirmNewEmail = Locale.getKey(
        'lvcAccountSecurity.errors.changeEmail.confirmNewEmail'
      )
    }
    if (!newEmail) {
      this.errors.changeEmail.newEmail = Locale.getKey(
        'lvcAccountSecurity.errors.changeEmail.newEmail'
      )
    }

    if (newEmail && !EMAIL_REGEX.test(newEmail)) {
      this.errors.changeEmail.newEmail = Locale.getKey(
        'lvcAccountSecurity.errors.changeEmail.newEmail'
      )
    }

    if (!password) {
      this.errors.changeEmail.password = Locale.getKey(
        'lvcAccountSecurity.errors.changeEmail.password'
      )
    }

    let hasError = false
    for (const key in this.errors.changeEmail) {
      if (this.errors.changeEmail[key]) {
        hasError = true
      }
    }

    if (hasError) {
      this.rerender()
      return
    }

    let session

    try {
      session = await reauthenticateWithCredential(
        auth.getUserAuth(),
        EmailAuthProvider.credential(email, password)
      )
    } catch (error) {
      this.errors.changeEmail.password = Locale.getKey(
        'lvcAccountSecurity.errors.incorrectOldPassword'
      )
      this.rerender()
      return
    }

    if (!session) {
      this.errors.changeEmail.password = Locale.getKey(
        'lvcAccountSecurity.errors.incorrectOldPassword'
      )
      this.rerender()
      return
    }

    try {
      const mainModule = MainModule.getInstance()
      try {
        await mainModule.getAuth().changeEmail(newEmail)
        mainModule.showToastAlert(
          Locale.getKey('lvcAccountSecurity.changeEmail.emailSentNotification')
        )
      } catch (e) {}
      this.changeEmailDrawer = true
      passwordElement.value = ''
      newEmailElement.value = ''
      confirmNewEmailElement.value = ''
      MainModule.getInstance().getRouting().clearDirty()
      this.rerender()
    } catch (error) {
      this.errors.changeEmail.newPassword = Locale.getKey('lvcAccountSecurity.errors.misc')
      this.rerender()
    }
  }

  save = async () => {
    const mainModule = MainModule.getInstance()
    const auth = mainModule.getAuth()
    const userAuth = mainModule.getAuth().getUserAuth()
    const email = userAuth.email
    const oldPasswordElement = this.select('[name="oldPassword"]') as HTMLInputElement
    const newPasswordElement = this.select('[name="newPassword"]') as HTMLInputElement
    const confirmPasswordElement = this.select('[name="confirmPassword"]') as HTMLInputElement
    const oldPassword = unescapeHtml(oldPasswordElement.value)
    const newPassword = unescapeHtml(newPasswordElement.value)
    const confirmPassword = unescapeHtml(confirmPasswordElement.value)
    for (const key in this.errors) {
      this.errors[key] = false
    }

    if (newPassword !== confirmPassword) {
      this.errors.confirmPassword = Locale.getKey('lvcAccountSecurity.errors.confirmPassword')
    }
    if (!newPassword) {
      this.errors.newPassword = Locale.getKey('lvcAccountSecurity.errors.newPassword')
    }

    if (newPassword && newPassword.length < 8) {
      this.errors.newPassword = Locale.getKey('lvcAccountSecurity.errors.passwordLength')
    }

    if (newPassword && !PASSWORD_REGEX.test(newPassword)) {
      this.errors.newPassword = Locale.getKey('lvcAccountSecurity.errors.passwordNotRespected')
    }

    if (!oldPassword) {
      this.errors.oldPassword = Locale.getKey('lvcAccountSecurity.errors.oldPassword')
    }

    let hasError = false
    for (const key in this.errors) {
      if (this.errors[key]) {
        hasError = true
      }
    }

    if (hasError) {
      this.rerender()
      return
    }

    let session

    try {
      session = await reauthenticateWithCredential(
        auth.getUserAuth(),
        EmailAuthProvider.credential(email, oldPassword)
      )
    } catch (error) {
      this.errors.oldPassword = Locale.getKey('lvcAccountSecurity.errors.incorrectOldPassword')
      this.rerender()
      return
    }

    if (!session) {
      this.errors.oldPassword = Locale.getKey('lvcAccountSecurity.errors.incorrectOldPassword')
      this.rerender()
      return
    }

    try {
      await updatePassword(auth.getUserAuth(), newPassword)

      session = await reauthenticateWithCredential(
        auth.getUserAuth(),
        EmailAuthProvider.credential(email, newPassword)
      )
      if (!session) {
        throw new Error('User not found')
      }
      this.changePasswordDrawer = true
      oldPasswordElement.value = ''
      newPasswordElement.value = ''
      confirmPasswordElement.value = ''
      let mainModule = MainModule.getInstance()
      mainModule.getRouting().clearDirty()
      mainModule.showToastAlert(Locale.getKey('lvcAccountSecurity.passwordChanged'))
      this.rerender()
    } catch (error) {
      this.errors.newPassword = Locale.getKey('lvcAccountSecurity.errors.misc')
      this.rerender()
    }
    mainModule.getRouting().clearDirty()
  }

  removeProvider = async (providerId: string) => {
    const mainModule = MainModule.getInstance()
    const auth = mainModule.getAuth()

    if (!(this.providers as Array<any>)?.find(provider => provider.providerId === 'password')) {
      this.unlinkingProvider = providerId
      return (this.unlinkProviderErrorModalOpened = true)
    }

    const fbAuth = auth.getFirebaseAuth()

    try {
      await unlink(fbAuth.currentUser, providerId)
      this.updateInfo()
      this.rerender()
    } catch (error) {
      console.error(error)
    }
  }

  linkProvider = async (providerId: string) => {
    const mainModule = MainModule.getInstance()
    const auth = mainModule.getAuth()
    const fbAuth = auth.getFirebaseAuth()
    let provider: any

    if (providerId === 'google.com') {
      provider = new GoogleAuthProvider()
    } else if (providerId === 'facebook.com') {
      provider = new FacebookAuthProvider()
    }

    try {
      await linkWithRedirect(fbAuth.currentUser, provider)
      this.rerender()
    } catch (error) {
      console.error(error)
    }
  }

  async onProviderToggle(toggledState: boolean, providerId: string): Promise<void> {
    if (toggledState) {
      this.linkProvider(providerId)
    } else {
      this.removeProvider(providerId)
    }
  }

  openDeleteAccountModal = (privacyPolicy?: boolean, termsAndConditions?: boolean) => {
    const passwordProvider = (this.providers as Array<any>)?.find(
      provider => provider.providerId === 'password'
    )
    let attr: { name: string; value: string }[] = []
    if (privacyPolicy) {
      attr.push({
        name: 'delete-confirmation-privacy-policy-mode',
        value: 'true',
      })
    }
    if (termsAndConditions) {
      attr.push({
        name: 'delete-confirmation-terms-and-conditions-mode',
        value: 'true',
      })
    }
    if (passwordProvider) {
      attr.push({ name: 'password-provider', value: 'true' })
    }
    MainModule.getInstance().injectIntoDOM('lvc-delete-profile-popup', attr)
  }

  closeUnlinkProviderModal = () => {
    this.unlinkProviderErrorModalOpened = false
  }

  submitCreatePasswordRequest = async () => {
    if (this.onCreatePasswordBusy) return

    this.onCreatePasswordBusy = true
    this.rerender()
    const mainModule = MainModule.getInstance()
    try {
      await mainModule.getAuth().createPassword()
    } catch (e) {}
    mainModule.showToastAlert(Locale.getKey('lvcAccountSecurity.createPasswordNotification'))
    this.onCreatePasswordBusy = false
    this.unlinkProviderErrorModalOpened = false
    this.rerender()
  }

  deleteAccount = async (event: Event) => {
    const mainModule = MainModule.getInstance()
    const auth = mainModule.getAuth()
    const detail = (event as CustomEvent).detail
    try {
      this.deleteConfirmationPasswordError = false
      const email = this.email as string
      const passwordProvider = (this.providers as Array<any>)?.find(
        provider => provider.providerId === 'password'
      )
      if (passwordProvider) {
        const password = detail.password as string
        await reauthenticateWithCredential(
          auth.getUserAuth(),
          EmailAuthProvider.credential(email, password)
        )
      }
      let response: any = await auth.deleteAccount(detail.reason || 'none')
      if (response?.success) mainModule.getRouting().goTo(`${Routing.HOST}?account-deleted=true`)
    } catch (e) {
      this.deleteConfirmationPasswordError = Locale.getKey(
        'lvcAccountSecurity.deleteAccountConfirmationModalPasswordError'
      )
      Minze.cast('lvc:delete_profile_error', { error: this.deleteConfirmationPasswordError })
    }
  }

  closePrivacyPolicyTooltip = () => (this.privacyPolicyTooltipOpened = false)
  openPrivacyPolicyTooltip = () => (this.privacyPolicyTooltipOpened = true)

  closeTermsAndConditionsTooltip = () => (this.termsAndConditionsTooltipOpened = false)
  openTermsAndConditionsTooltip = () => (this.termsAndConditionsTooltipOpened = true)

  capitalizeFirstLetter = (string: string) => {
    return string.charAt(0).toUpperCase() + string.slice(1)
  }

  displayConsentToggle = (title: string, name: string) => {
    return /* html */ `
      <div class="account-security-consent">
        <lvc-checkbox
          name="${name}"
          label="${title}"
          default-value="true"
          data-action="${'deleteAccount' + this.capitalizeFirstLetter(name) + 'Mode'}"
        ></lvc-checkbox>
        <lvc-help-icon name="${name + 'TooltipToggle'}"></lvc-help-icon>

        ${
          this[`${name}TooltipOpened`]
            ? /* html */ `
          <div class="close-overlay" data-name="${name + 'TooltipOverlay'}"></div>
        `
            : ''
        }
        
        <lvc-box ml="4px" pt="2px" position="relative">
          <dialog class="tooltip" ${this[`${name}TooltipOpened`] ? 'open' : ''}>
            <h3>${Locale.getKey(`lvcAccountSecurity.deleteAccount${this.capitalizeFirstLetter(name)}Mode.tooltipTitle`)}</h3>
            <p>${Locale.getKey(`lvcAccountSecurity.deleteAccount${this.capitalizeFirstLetter(name)}Mode.tooltipText`)}</p>
            <a href="${Locale.getKey(`lvcAccountSecurity.deleteAccount${this.capitalizeFirstLetter(name)}Mode.tooltipUrl`)}" target="_blank">
              ${Locale.getKey(`lvcAccountSecurity.deleteAccount${this.capitalizeFirstLetter(name)}Mode.tooltipLink`)}
            </a>
          </dialog>
        </lvc-box>
      </div>
    `
  }

  html = () => {
    let providers = this.providers as Array<any>
    let googleProviderInstance = new GoogleAuthProvider()
    let facebookProviderInstance = new FacebookAuthProvider()
    let passwordProvider = providers.find((provider: any) => provider.providerId === 'password')
    let facebookProvider = providers.find((provider: any) => provider.providerId === 'facebook.com')
    let googleProvider = providers.find((provider: any) => provider.providerId === 'google.com')

    return /* html */ `
      <div class="account-security">
        <lvc-account-header
          label="${Locale.getKey('lvcAccountSecurity.title')}"
          separator="${this.titleSeparator}"
        >
          <lvc-account-key-icon width="44" height="44" slot="icon"></lvc-account-key-icon>
        </lvc-account-header>

        <div class="account-security-wrapper">
          <div class="account-security-inputs">
            <lvc-hand-drawn-input label="${Locale.getKey('lvcAccountSecurity.email')}" value="${this.email}" readOnly="true">
              <div slot="action">
                <button class="account-security-drawer-toggle" name="emailDrawerToggle">
                  ${
                    this.changeEmailDrawer
                      ? /* html */ `
                    <lvc-pencil-icon></lvc-pencil-icon> ${Locale.getKey('lvcAccountSecurity.modify')}
                  `
                      : /* html */ `
                    <lvc-close-icon width="20px" height="20px"></lvc-close-icon> ${Locale.getKey('lvcAccountSecurity.cancel')}
                  `
                  }
                </button>
              </div>
            </lvc-hand-drawn-input>
            ${
              !this.changeEmailDrawer
                ? /* html */ `
              <lvc-hand-drawn-box variant="box2">
                <div class="account-security-drawer">
                  <div class="account-security-drawer-content">
                    <lvc-input
                      name="newEmailPassword"
                      type="password"
                      placeholder="${Locale.getKey('lvcAccountSecurity.changeEmail.password')}"
                      error="${this.errors.changeEmail.password}"
                      default-value=""
                      label="${Locale.getKey('lvcAccountSecurity.changeEmail.password')}"
                      on-change="${this.onChangeCb}"
                    ></lvc-input>
                    <lvc-input
                      name="newEmail"
                      type="email"
                      placeholder="${Locale.getKey('lvcAccountSecurity.changeEmail.newEmail')}"
                      error="${this.errors.changeEmail.newEmail}"
                      default-value=""
                      label="${Locale.getKey('lvcAccountSecurity.changeEmail.newEmail')}"
                      on-change="${this.onChangeCb}"
                    ></lvc-input>
                    <lvc-input
                      name="confirmNewEmail"
                      type="email"
                      placeholder="${Locale.getKey('lvcAccountSecurity.changeEmail.confirmNewEmail')}"
                      error="${this.errors.changeEmail.confirmNewEmail}"
                      default-value=""
                      label="${Locale.getKey('lvcAccountSecurity.changeEmail.confirmNewEmail')}"
                      on-change="${this.onChangeCb}"
                    ></lvc-input>
                  </div>
                  <div class="account-security-drawer-footer">
                    <lvc-button width="100%" name="updateEmailSubmit">
                      ${Locale.getKey('lvcAccountSecurity.save')}
                    </lvc-button>
                  </div>
                </div>
              </lvc-hand-drawn-box>
            `
                : ''
            }

            <lvc-hand-drawn-input label="${Locale.getKey('lvcAccountSecurity.password')}" value="****************" readOnly="true">
              <div slot="action">
                ${
                  passwordProvider
                    ? /* html */ `
                  <button class="account-security-drawer-toggle" name="passwordDrawerToggle">
                    ${
                      this.changePasswordDrawer
                        ? /* html */ `
                      <lvc-pencil-icon></lvc-pencil-icon> ${Locale.getKey('lvcAccountSecurity.modify')}
                    `
                        : /* html */ `
                      <lvc-close-icon width="20px" height="20px"></lvc-close-icon> ${Locale.getKey('lvcAccountSecurity.cancel')}
                    `
                    }
                  </button>
                `
                    : /* html */ `
                  <button id="create-password-button" class="account-security-drawer-toggle ${this.onCreatePasswordBusy ? 'busy' : ''}" name="createPasswordButton">
                    ${Locale.getKey('lvcAccountSecurity.createPassword')}
                  </button>
                `
                }
              </div>
            </lvc-hand-drawn-input>
            ${
              !this.changePasswordDrawer
                ? /* html */ `
              <lvc-hand-drawn-box variant="box2">
                <div class="account-security-drawer">
                  <div class="account-security-drawer-content">
                    <lvc-input
                      name="oldPassword"
                      type="password"
                      placeholder="${Locale.getKey('lvcAccountSecurity.oldPassword')}"
                      error="${this.errors.oldPassword}"
                      default-value=""
                      label="${Locale.getKey('lvcAccountSecurity.oldPassword')}"
                      on-change="${this.onChangeCb}"
                    ></lvc-input>
                    <lvc-input
                      name="newPassword"
                      type="password"
                      placeholder="${Locale.getKey('lvcAccountSecurity.newPassword')}"
                      error="${this.errors.newPassword}"
                      default-value=""
                      label="${Locale.getKey('lvcAccountSecurity.newPassword')}"
                      on-change="${this.onChangeCb}"
                    ></lvc-input>
                    <lvc-input
                      name="confirmPassword"
                      type="password"
                      placeholder="${Locale.getKey('lvcAccountSecurity.confirmPassword')}"
                      error="${this.errors.confirmPassword}"
                      default-value=""
                      label="${Locale.getKey('lvcAccountSecurity.confirmPassword')}"
                      on-change="${this.onChangeCb}"
                    ></lvc-input>
                  </div>
                  <div class="account-security-drawer-footer">
                    <lvc-button width="100%" name="save">
                      ${Locale.getKey('lvcAccountSecurity.save')}
                    </lvc-button>
                  </div>
                </div>
              </lvc-hand-drawn-box>
            `
                : ''
            }
          </div>

          <div>
            <h2 class="account-security-section-title">${Locale.getKey('lvcAccountSecurity.providerConnection')}</h2>
            <div class="account-security-providers">
              <div class="account-security-provider">
                <div class="account-security-provider-label">${Locale.getKey('lvcAccountSecurity.providers.google')}</div>
                <div class="account-security-provider-toggle">
                  <lvc-toggle
                    id="${googleProviderInstance?.providerId}"
                    class="toggle"
                    on-click="${this.toggleCb}"
                    initial-toggled="${googleProvider}"
                  ></lvc-toggle>
                </div>
              </div>
              <div class="account-security-provider">
                <div class="account-security-provider-label">${Locale.getKey('lvcAccountSecurity.providers.facebook')}</div>
                <div class="account-security-provider-toggle">
                  <lvc-toggle
                    id="${facebookProviderInstance?.providerId}"
                    class="toggle"
                    on-click="${this.toggleCb}"
                    initial-toggled="${facebookProvider}"
                  ></lvc-toggle>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h2 class="account-security-section-title">${Locale.getKey('lvcAccountSecurity.legalInformationTitle')}</h2>
            <div class="account-security-legal">
              <div class="account-security-terms">
                ${this.displayConsentToggle(Locale.getKey('lvcAccountSecurity.termsAndConditions'), 'termsAndConditions')}
                ${this.displayConsentToggle(Locale.getKey('lvcAccountSecurity.privacyPolicy'), 'privacyPolicy')}
              </div>
              <div class="account-security-delete">
                <lvc-account-button-delete></lvc-account-button-delete>
              </div>
            </div>

            ${
              this.unlinkProviderErrorModalOpened
                ? /* html */ `
              <lvc-closeable-modal max-width="512px" no-close-button="true">
                <h3 class="delete-account-title">
                  ${Locale.getKey(`lvcAccountSecurity.unlinkProviderErrorModal.title.${this.unlinkingProvider}`)}
                </h3>
                <p class="delete-account-text">
                  ${Locale.getKey(`lvcAccountSecurity.unlinkProviderErrorModal.text.${this.unlinkingProvider}`)}
                </p>
                <div class="delete-account-buttons">
                  <lvc-button
                    border="1px solid #1E007D"
                    padding="14px 24px"
                    theme="transparent"
                    width="100%"
                    class="button"
                    id="unlink-provider-cancel-button"
                    type="submit"
                  >
                    ${Locale.getKey(`lvcAccountSecurity.unlinkProviderErrorModal.buttons.cancel`)}
                  </lvc-button>
                  <lvc-button
                    padding="14px 24px"
                    width="100%"
                    class="button"
                    id="unlink-provider-confirm-button"
                    type="submit"
                  >
                    ${Locale.getKey(`lvcAccountSecurity.unlinkProviderErrorModal.buttons.submit`)}
                  </lvc-button>
                </div>
              </lvc-closeable-modal>
            `
                : ``
            }
          </div>
        </div>
      </div>
    `
  }

  eventListeners: EventListeners = [
    [window, 'lvc:user_state_changed', this.handleAuthChanged.bind(this)],
    [window, 'lvc:delete_profile_confirmed', this.deleteAccount.bind(this)],
    ['[name="save"]', 'click', this.save.bind(this)],
    ['[name="updateEmailSubmit"]', 'click', this.changeEmail.bind(this)],
    ['[name="passwordDrawerToggle"]', 'click', this.onPasswordDrawerToggle.bind(this)],
    ['[name="emailDrawerToggle"]', 'click', this.onEmailDrawerToggle.bind(this)],
    ['input', 'change', this.onInputChange.bind(this)],
    ['lvc-help-icon[name="privacyPolicyTooltipToggle"]', 'click', this.openPrivacyPolicyTooltip],
    [
      '.close-overlay[data-name="privacyPolicyTooltipOverlay"]',
      'click',
      this.closePrivacyPolicyTooltip,
    ],
    [
      'lvc-help-icon[name="termsAndConditionsTooltipToggle"]',
      'click',
      this.openTermsAndConditionsTooltip,
    ],
    [
      '.close-overlay[data-name="termsAndConditionsTooltipOverlay"]',
      'click',
      this.closeTermsAndConditionsTooltip,
    ],
    ['#unlink-provider-cancel-button', 'click', this.closeUnlinkProviderModal.bind(this)],
    ['#unlink-provider-confirm-button', 'click', this.submitCreatePasswordRequest.bind(this)],
    ['#create-password-button', 'click', this.submitCreatePasswordRequest.bind(this)],
    [
      '[data-action="deleteAccountPrivacyPolicyMode"]',
      'click',
      event => {
        event.preventDefault()
        this.openDeleteAccountModal(true)
      },
    ],
    [
      '[data-action="deleteAccountTermsAndConditionsMode"]',
      'click',
      event => {
        event.preventDefault()
        this.openDeleteAccountModal(false, true)
      },
    ],
  ]

  css = () => `
    :host {}

    .account-security {}

    .account-security-wrapper {
      display: flex;
      flex-direction: column;
      gap: 30px;
    }

    .account-security-inputs {
      display: flex;
      flex-direction: column;
      gap: 15px;
    }

    .account-security-drawer-toggle {
      display: flex;
      align-items: center;
      gap: 5px;
      padding: 0px;
      background: transparent;
      border: none;
      color: #1E007D;
      font-family: inherit;
      font-size: 0.875em;
      font-weight: 500;
      text-decoration: underline;
      text-underline-offset: 4px;
      cursor: pointer;
    }

    .account-security-drawer-toggle:hover {
      text-decoration: none;
    }

    .account-security-drawer {
      padding: 10px;
    }

    .account-security-drawer-content {
      display: flex;
      flex-direction: column;
      gap: 15px;
    }

    .account-security-drawer-footer {
      margin-top: 15px;
    }

    .account-security-section-title {
      color: #1E007D;
      font-size: 0.875em;
      font-weight: 700;
      line-height: 120%;
    }

    .account-security-providers {
      display: flex;
      flex-direction: column;
      gap: 15px;
    }

    .account-security-provider {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .account-security-provider-label {
      color: #1E007D;
      font-weight: 800;
      text-transform: uppercase;
    }

    .account-security-provider-toggle {}

    .account-security-legal {
      display: flex;
      flex-direction: column;
      gap: 40px;
    }

    .account-security-terms {
      display: flex;
      flex-direction: column;
      gap: 15px;
    }

    .account-security-consent {
      position: relative;
      display: flex;
      align-items: center;
      gap: 5px;
    }

    .account-security-consent lvc-checkbox {
      width: auto;
    }

    lvc-help-icon {
      cursor: pointer;
    }

    .tooltip {
      z-index: 1;
      border: 1px solid rgba(30, 0, 125, 0.3);
      box-shadow: 0px 4px 16px rgba(15, 0, 62, 0.15);
      border-radius: 8px;
      padding: 16px;
      font-weight: ${Font.weight.medium};
      font-size: 14px;
      line-height: 150%;
      color: #1E007D;
      max-width: 90vw;
      width: 340px;
      top: -5px;
      transform: translateY(-100%);
    }

    .tooltip h3 {
      margin-top: 0;
    }

    .close-overlay {
      position: fixed;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      z-index: 1;
    }

    .account-security-delete button.delete {
      display: flex;
      align-items: center;
      gap: 5px;
      padding: 0px;
      background: transparent;
      border: none;
      color: #1E007D;
      font-family: inherit;
      font-size: 0.875em;
      font-weight: 500;
      text-decoration: underline;
      text-underline-offset: 4px;
      cursor: pointer;
    }

    .account-security-delete button.delete:hover {
      text-decoration: none;
    }
    
    @media screen and (max-width: 991px) {
      
    }

    @media only screen and (min-width: 992px) {
      .account-security-wrapper { 
        gap: 40px;
      }

      .account-security-drawer-content {
        gap: 30px;
      }

      .account-security-drawer {
        padding: 20px;
      }

      .account-security-drawer-footer {
        margin-top: 30px;
      }

      .account-security-providers {
        flex-direction: row;
        gap: 60px;
      }

      .account-security-provider {
        justify-content: auto;
        gap: 30px;
      }

      .account-security-legal {
        flex-direction: row;
        justify-content: space-between;
      }

      .account-security-terms {
        flex-direction: row;
        gap: 40px;
      }
    }
  `
}
